import { ChevronDown, ChevronRight, Folder, Loader2, X } from "lucide-react";
import React, { memo, useEffect, useMemo } from "react";

import { TreeNode, useFolderTree } from "../hooks/useFolderTree";

import { useFetchFolderList } from "@/common/services/scenarios";
import { findPathTitlesToNode } from "@/common/utils/utils";
import { FilterDrawerPanel } from "@/components/explorer/FilterDrawerPanel";
import {
  Chip,
  FilterGroup,
} from "@/pages/scenarios/all-scenarios/folder/components/ExplorerFilters";

interface FolderPickerPanelProps {
  versionId?: number;
  /** Folder domain — test-case folders use "TESTCASE" (matches FolderView). */
  folderType?: string;
  selectedFolderId?: number | null;
  onSelect: (folderId: number, pathTitles: string[]) => void;
  onClose?: () => void;
}

interface FolderPickerRowProps {
  node: TreeNode;
  depth: number;
  isOpen: boolean;
  isActive: boolean;
  isLoadingChildren: boolean;
  onToggle: (node: TreeNode) => void;
  onSelect: (node: TreeNode) => void;
}

const FolderPickerRow: React.FC<FolderPickerRowProps> = memo(
  ({
    node,
    depth,
    isOpen,
    isActive,
    isLoadingChildren,
    onToggle,
    onSelect,
  }) => (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(node)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(node);
        }
      }}
      className={`group flex items-center gap-1.5 py-1 cursor-pointer text-[12px] font-normal transition-colors ${
        isActive
          ? "bg-primary/10 text-primary"
          : "text-foreground hover:bg-muted/60"
      }`}
      style={{ paddingLeft: `${depth * 16 + 12}px`, paddingRight: "12px" }}
    >
      <span
        role="presentation"
        onClick={(e) => {
          e.stopPropagation();
          onToggle(node);
        }}
        className="grid shrink-0 place-items-center"
      >
        {isLoadingChildren ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
        ) : isOpen ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
      </span>

      <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />

      <span className="truncate flex-1 min-w-0">{node.title}</span>

      {typeof node.count === "number" && node.count > 0 && (
        <span className="ml-auto shrink-0 text-[10px] tabular-nums text-muted-foreground">
          {node.count}
        </span>
      )}
    </div>
  ),
);
FolderPickerRow.displayName = "FolderPickerRow";

/**
 * Self-contained folder tree used inside the Create Test dialog to let the user
 * pick a different destination folder. Lazily loads sub-folders on expand and
 * reports the selected folder id + its full title path.
 */
const FolderPickerPanel: React.FC<FolderPickerPanelProps> = ({
  versionId,
  folderType = "TESTCASE",
  selectedFolderId,
  onSelect,
  onClose,
}) => {
  const [showFilters, setShowFilters] = React.useState(false);
  const [folderScope, setFolderScope] = React.useState<"all" | "roots">("all");
  const { data: rootFolders, isLoading } = useFetchFolderList(
    versionId,
    folderType,
  );

  const {
    folderTree,
    initializeTree,
    toggleCollapse,
    isNodeCollapsed,
    isNodeLoading,
  } = useFolderTree();

  useEffect(() => {
    if (isLoading) return;
    const folders = (rootFolders as any)?.data ?? [];
    const rootOnly = folders.filter((f: any) => f.parent_id == null);
    initializeTree(rootOnly.length ? rootOnly : folders);
  }, [rootFolders, isLoading, initializeTree]);

  const handleSelect = (node: TreeNode) => {
    const path = findPathTitlesToNode(folderTree, node.id) ?? [node.title];
    onSelect(node.id, path);
  };
  const activeFiltersCount = folderScope === "all" ? 0 : 1;

  const renderTree = useMemo(() => {
    const render = (nodes: TreeNode[], depth = 0): React.ReactNode =>
      nodes.map((node) => {
        const collapsed = isNodeCollapsed(node.id);
        const open = !collapsed;
        return (
          <React.Fragment key={node.id}>
            <FolderPickerRow
              node={node}
              depth={depth}
              isOpen={open}
              isActive={selectedFolderId === node.id}
              isLoadingChildren={isNodeLoading(node.id)}
              onToggle={(n) => toggleCollapse(n.id, n)}
              onSelect={handleSelect}
            />
            {open && node.children && node.children.length > 0 && (
              <div>{render(node.children, depth + 1)}</div>
            )}
          </React.Fragment>
        );
      });
    return render;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderTree, selectedFolderId, isNodeCollapsed, isNodeLoading]);

  const visibleTree =
    folderScope === "roots"
      ? folderTree.map((node) => ({ ...node, children: [] }))
      : folderTree;

  return (
    <div className="flex h-full w-65 shrink-0 flex-col border-r border-border bg-muted/20">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <div className="text-[12.5px] font-semibold text-foreground">
          Select folder
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close folder picker"
            className="grid size-6 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
      <FilterDrawerPanel
        activeCount={activeFiltersCount}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters((s) => !s)}
        onClearAll={() => setFolderScope("all")}
        headerClassName="px-3"
        chips={
          folderScope !== "all" ? (
            <Chip onClear={() => setFolderScope("all")}>
              Scope: Root folders
            </Chip>
          ) : null
        }
        drawerContent={
          <FilterGroup
            label="Scope"
            options={[{ id: "roots", label: "Root folders only" }]}
            selected={folderScope === "all" ? [] : [folderScope]}
            onToggle={(id) =>
              setFolderScope((prev) =>
                prev === id ? "all" : (id as typeof folderScope),
              )
            }
          />
        }
      />
      <div className="flex-1 overflow-y-auto py-1 scrollbar-thin">
        {isLoading ? (
          <div className="flex items-center gap-1.5 px-4 py-3 text-[12px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading folders…
          </div>
        ) : folderTree.length === 0 ? (
          <div className="px-4 py-3 text-[12px] text-muted-foreground">
            No folders found.
          </div>
        ) : (
          renderTree(visibleTree)
        )}
      </div>
    </div>
  );
};

export default FolderPickerPanel;
